import { Component, Host, Prop, h, Element, State, Watch, Event, EventEmitter } from '@stencil/core';
import { ripple, PnButtonAppearance, PnButtonVariant } from '@/index';

/**
 * The `pn-button` is built with a native `button` or `a` element, if you use the `href` attribute.
 *
 * @nativeClick Use the `click` event to listen when the button is clicked.
 */
@Component({
  tag: 'pn-button',
  styleUrl: 'pn-button.scss',
})
export class PnButton {
  buttonEl: HTMLElement;
  tooltipEl: HTMLDivElement;
  tooltipDx: number;

  openEvents: string[] = ['mouseover', 'focus'];
  closeEvents: string[] = ['mouseleave', 'blur', 'touchcancel', 'touchleave'];
  eventListeners: string[] = [...this.openEvents, ...this.closeEvents];

  @Element() hostElement: HTMLElement;

  @State() tooltipOpen: boolean = false;
  @State() tooltipUpwards: boolean = false;

  /** The label of the button, this is the same as using the default slot. */
  @Prop() label?: string = '';
  /**
   * Select the button appearance: default (blue), `light` (white) and  `warning` (red).
   *
   * @category Visual
   */
  @Prop() appearance?: PnButtonAppearance = '';
  /**
   * Select the button variant: default (colored background), `outlined` (bordered, no background) and `borderless` (borderless, no background).
   *
   * @category Visual
   */
  @Prop() variant?: PnButtonVariant = '';
  /** Use the small button. @category Visual */
  @Prop() small: boolean = false;
  /** Display the loading indicator. @category Visual */
  @Prop({ reflect: true }) loading: boolean = false;

  /**
   * The SVG content of the icon you want.
   * @see {@link https://portal.postnord.com/web-components/?path=/docs/docs-assets--docs pn-design-assets}
   * @category Icon
   */
  @Prop() icon?: string;
  /**
   * Turn the button into an icon only. Requires `icon` and one of the following props to work: `tooltip`, `arialabel` or `arialabelledby`.
   * @summary __You need an `icon` selected, as well as either `tooltip`, `arialabel` or `arialabelledby` for this to work__.
   * @see {@link icon}
   * @category Icon
   */
  @Prop() iconOnly: boolean = false;
  /**
   * Place the `icon` to the left of the button.
   *
   * @summary __You need an `icon` selected for this to work__.
   * @see {@link icon}
   * @category Icon
   */
  @Prop() leftIcon: boolean = false;

  /**
   * When the user focus/hover the button, the `tooltip` text will show up.
   *
   * @category Tooltip
   */
  @Prop() tooltip?: string;
  /**
   * Prefer the tooltip to open upwards. Will still go downwards if there is no space.
   * @category Tooltip
   */
  @Prop() tooltipUp: boolean = false;

  /** Select HTML button type. @category Button */
  @Prop() type?: 'button' | 'reset' | 'submit' | '' = 'button';
  /**
   * Connect this button to a HTML form.
   * @category Button
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#form HTML button form}
   */
  @Prop() form?: string;

  /** Pass an href to make the button into link (a-tag). @category Link */
  @Prop() href?: string;
  /** The rel attribute of the link. @category Link */
  @Prop() rel?: string;
  /** The target attribute of the link. @category Link */
  @Prop() target?: string;
  /** The download attribute of the link. @category Link */
  @Prop() download?: string;

  /** HTML ID. @category HTML attributes */
  @Prop() buttonId?: string = null;
  /** HTML aria-label attribute. @category HTML attributes */
  @Prop() arialabel?: string = null;
  /** HTML aria-labelledby attribute. @category HTML attributes */
  @Prop() arialabelledby?: string = null;
  /** HTML aria-current attribute. @category HTML attributes */
  @Prop() ariacurrent?: string = null;
  /** HTML aria-controls attribute. @category HTML attributes */
  @Prop() ariacontrols?: string = null;
  /** HTML aria-pressed attribute. @category HTML attributes */
  @Prop() ariapressed?: string = null;
  /** HTML aria-haspopup attribute. @category HTML attributes */
  @Prop() ariahaspopup?: string = null;
  /** HTML aria-expanded attribute. @category HTML attributes */
  @Prop() ariaexpanded?: string = null;
  /** HTML tabindex. @category HTML attributes */
  @Prop() noTab?: boolean = false;

  @Watch('tooltipOpen')
  openHandler() {
    if (this.tooltipOpen) this.checkTooltipPosition();
  }

  @Watch('tooltip')
  iconOnlyHandler() {
    this.checkTooltipPosition();
    if (this.tooltip) this.initTooltip();
    else this.removeTooltip();
  }

  /**
   * This event is fired when the `button`/`a` element of the `pn-button` is clicked.
   * Setting an eventListener for `click` on the `pn-button` will trigger if you click outside of the button area
   * or if the button is `disabled` with the `loading` prop.
   **/
  @Event() pnClick: EventEmitter<MouseEvent>;

  componentWillLoad() {
    this.tooltipUpwards = this.tooltipUp;
  }

  componentDidLoad() {
    this.iconOnlyHandler();
  }

  getRect(element: HTMLElement): DOMRect {
    return element.getBoundingClientRect();
  }

  initTooltip() {
    this.eventListeners.forEach(e => this.buttonEl.addEventListener(e, this.toggleTooltip));
  }

  removeTooltip() {
    this.eventListeners.forEach(e => this.buttonEl.removeEventListener(e, this.toggleTooltip));
  }

  toggleTooltip = (event: MouseEvent | FocusEvent) => {
    this.tooltipOpen = this.openEvents.includes(event.type);
  };

  setTooltipOffset() {
    this.tooltipEl.style.setProperty('--pn-dx', `${this.tooltipDx}px`);
  }

  checkTooltipPosition() {
    this.tooltipEl.style.transform = 'translateX(-50%)';
    this.tooltipUpwards = this.tooltipUp;

    requestAnimationFrame(() => {
      const { top, x, y, right, height } = this.getRect(this.tooltipEl);
      const extraMargin = 16;
      this.tooltipDx = 0;

      this.tooltipUpwards = this.tooltipUp ? top > height : y + height > window.innerHeight;
      this.tooltipEl.style.transform = '';

      if (x < 0) this.tooltipDx = x * -1 + extraMargin;
      if (right > window.innerWidth) this.tooltipDx = window.innerWidth - right - extraMargin - 8;

      requestAnimationFrame(() => this.setTooltipOffset());
    });
  }

  getAttributes() {
    const attrs = this.href
      ? {
          'href': this.href,
          'rel': !this.rel && this.target === '_blank' ? 'noopener' : this.rel,
          'target': this.target ?? null,
          'download': this.download ?? null,
          'aria-current': this.ariacurrent,
          'aria-disabled': this.loading ? 'true' : null,
        }
      : {
          'type': this.type,
          'form': this.form,
          'disabled': this.loading,
          'aria-pressed': this.ariapressed,
          'aria-controls': this.ariacontrols,
          'aria-haspopup': this.ariahaspopup,
          'aria-expanded': this.ariaexpanded,
        };

    const appearances: PnButtonAppearance[] = ['light', 'warning'];
    const variations: PnButtonVariant[] = ['borderless', 'outlined'];

    return {
      'tabindex': this.noTab || (this.href && this.loading) ? '-1' : null,
      'data-small': this.small,
      'data-loading': this.loading,
      'data-tooltip-open': this.tooltipOpen,
      'data-tooltip-upwards': this.tooltipUpwards,
      'data-icon-only': this.showIconOnly(),
      'data-left-icon': this.leftIcon,
      'data-appearance': appearances.includes(this.appearance) ? this.appearance : null,
      'data-variant': variations.includes(this.variant) ? this.variant : null,
      ...attrs,
    };
  }

  getTagName() {
    return this.href ? 'a' : 'button';
  }

  showIconOnly(): boolean {
    return !!this.icon && this.iconOnly && !!(this.tooltip || this.arialabel || this.arialabelledby);
  }

  handleClick(event: MouseEvent) {
    ripple(event, this.hostElement, '.pn-button-bg');
    this.pnClick.emit(event);
  }

  render() {
    const TagName = this.getTagName();

    return (
      <Host>
        <TagName
          class="pn-button"
          id={this.buttonId}
          aria-label={this.arialabel}
          aria-labelledby={this.arialabelledby}
          onClick={e => this.handleClick(e)}
          {...this.getAttributes()}
          ref={(el: HTMLElement) => (this.buttonEl = el)}
        >
          <div class="pn-button-bg"></div>
          <div class="pn-button-content">
            <div class="pn-button-text" hidden={this.showIconOnly()}>
              {this.label}
              <slot />
            </div>
            {!!this.icon && <pn-icon icon={this.icon} />}
          </div>
          <pn-spinner light />

          <div class="pn-button-tooltip" hidden={!this.tooltip} ref={el => (this.tooltipEl = el)}>
            <span class="pn-button-tooltip-text">{this.tooltip}</span>
          </div>
        </TagName>
      </Host>
    );
  }
}
